import React, { useState, useEffect, useRef } from "react";
import { Form, ListGroup, Spinner } from "react-bootstrap";

export default function SearchSelect({
  placeholder = "Search...",
  fetchItems,
  onSelect,
  itemRenderer = (item) => item.word || item.label || "Unnamed",
}) {
  const [searchText, setSearchText] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef(null);

  // Fetch filtered items based on searchText
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (!searchText.trim()) {
        setItems([]);
        return;
      }

      setLoading(true);
      fetchItems(searchText)
        .then(setItems)
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchText, fetchItems]);

  // Hide dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || items.length === 0) return;

    if (e.key === "ArrowDown") {
      setHighlightedIndex((prev) => (prev + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      setHighlightedIndex((prev) => (prev - 1 + items.length) % items.length);
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      handleSelect(items[highlightedIndex]);
    }
  };

  const handleSelect = (item) => {
    onSelect(item);
    setSearchText(item.label || item.word || "");
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e) => {
    setSearchText(e.target.value);
    setShowDropdown(true);
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: "relative",
        width: "100%",
        maxWidth: "500px",
        zIndex: 2, // ensure dropdown is on top
      }}
    >
      <Form.Control
        type="text"
        placeholder={placeholder}
        value={searchText}
        onChange={handleInputChange}
        onFocus={() => setShowDropdown(true)}
        onKeyDown={handleKeyDown}
        autoComplete="off"
      />

      {showDropdown && (
        <ListGroup
           style={{
            position: "absolute",
            top: "100%",
            left: 0,
            width: "100%",
            backgroundColor: "#fff",
            border: "1px solid #dee2e6",
            borderTop: "none",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            maxHeight: 240,
            overflowY: "auto",
            zIndex: 5, // Ensure dropdown overlaps
          }}
        >
          {loading && (
            <ListGroup.Item className="text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Loading...
            </ListGroup.Item>
          )}

          {!loading && items.length === 0 && (
            <ListGroup.Item className="text-muted">No results</ListGroup.Item>
          )}

          {items.map((item, idx) => (
            <ListGroup.Item
              key={item.id || idx}
              action
              active={idx === highlightedIndex}
              onMouseEnter={() => setHighlightedIndex(idx)}
              onMouseDown={() => handleSelect(item)} // use onMouseDown to avoid input blur
            >
              {itemRenderer(item)}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}
